import {
  Component,
  OnDestroy,
  OnInit,
  CUSTOM_ELEMENTS_SCHEMA,
  inject
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PdfViewerOpenOptions, PdfViewerService } from '../../../core/services/pdf-viewer.service';
import { StorageService } from '../../../core/services/storage.service';
import { NotificationService } from '../../../core/services/notification.service';

@Component({
  selector: 'app-pdf-viewer-modal',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './pdf-viewer-modal.component.html',
  styleUrls: ['./pdf-viewer-modal.component.css'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class PdfViewerModalComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly pdfViewerService = inject(PdfViewerService);
  private readonly storageService = inject(StorageService);
  private readonly notificationService = inject(NotificationService);
  private readonly translateService = inject(TranslateService);
  private readonly sanitizer = inject(DomSanitizer);

  open = false;
  title = '';
  pdfUrl: string | null = null;
  safePdfUrl: SafeResourceUrl | null = null;
  loading = false;

  ngOnInit(): void {
    this.pdfViewerService.request$
      .pipe(takeUntil(this.destroy$))
      .subscribe((request) => {
        if (!request) {
          this.reset();
          return;
        }
        this.loadRequest(request);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onOverlayClick(): void {
    this.close();
  }

  close(): void {
    this.pdfViewerService.close();
  }

  openInBrowser(): void {
    if (this.pdfUrl) {
      window.open(this.pdfUrl, '_blank', 'noopener,noreferrer');
    }
  }

  private loadRequest(request: PdfViewerOpenOptions): void {
    this.open = true;
    this.title = request.title;
    this.pdfUrl = null;
    this.loading = true;

    if (request.pdfUrl) {
      this.setPdfUrl(request.pdfUrl);
      this.loading = false;
      return;
    }

    if (!request.s3Key) {
      this.handleLoadError();
      return;
    }

    this.storageService
      .generateDownloadUrl(request.s3Key, { inline: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success && response.data?.downloadUrl) {
            this.setPdfUrl(response.data.downloadUrl);
          } else {
            this.handleLoadError();
          }
          this.loading = false;
        },
        error: () => {
          this.handleLoadError();
          this.loading = false;
        }
      });
  }

  private handleLoadError(): void {
    this.reset();
    this.notificationService.error(
      this.translateService.instant('dashboard.common.error'),
      this.translateService.instant('dashboard.common.pdfViewer.loadError')
    );
  }

  private setPdfUrl(url: string): void {
    this.pdfUrl = url;
    this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  private reset(): void {
    this.open = false;
    this.title = '';
    this.pdfUrl = null;
    this.safePdfUrl = null;
    this.loading = false;
  }
}
